import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';

import { errorMessage } from '../../MarketExplorer/api';
import {
  curateVersion,
  fetchProposal,
  fetchVersion,
  isJobFailed,
  jobMessage,
  pendingCurations,
  waitForCuration,
} from './api';
import CurvesChart from './CurvesChart';
import { DataBadge, DataCoverageSection, SavedExtras } from './DataCoverage';
import NewsByTurn from './NewsByTurn';
import {
  ChecksSection,
  InstrumentsSection,
  OverlapSection,
  PregradeSection,
  TraineeSection,
} from './PreviewSections';
import WeeklyGrid from './WeeklyGrid';
import { hasSavedExtras, isBlocker, plural, windowLine } from './format';
import { Proposal, ProposalVersion } from './types';
import './styles.css';

export type SaveTarget = {
  proposalId: number;
  versionId: number;
  title: string;
  /** Already loaded, so the save dialog can prefill without a read. */
  version?: ProposalVersion;
};

export type RejectTarget = {
  proposalId: number;
  title: string;
};

type Props = {
  /** The version to open; null closes the drawer. */
  versionId: number | null;
  /** Bumped by the parent after a save or reject, to re-read the status. */
  refreshKey?: number;
  onClose: () => void;
  onSave: (target: SaveTarget) => void;
  onReject: (target: RejectTarget) => void;
};

const Section: React.FC<{
  title: React.ReactNode;
  extra?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, extra, children }) => (
  <section className="blueprint-builder__section">
    <div className="blueprint-builder__section-head">
      <Typography.Title level={5}>{title}</Typography.Title>
      {extra}
    </div>
    {children}
  </section>
);

const STATUS_COLOURS: Record<Proposal['status'], string | undefined> = {
  open: 'processing',
  saved: 'success',
  rejected: 'default',
};

/**
 * The full preview of one proposal version. It reads `versions/{id}/` for the
 * preview and `proposals/{id}/` for the version list and status; switching
 * versions re-reads the preview. Admin-facing text (the thesis) and the
 * trainee-facing text are labelled apart. Data coverage sits right under the
 * header: a gap changes whether the rest of the preview can be trusted.
 */
const PreviewDrawer: React.FC<Props> = ({
  versionId,
  refreshKey = 0,
  onClose,
  onSave,
  onReject,
}) => {
  const [selectedId, setSelectedId] = useState<number | null>(versionId);
  const [version, setVersion] = useState<ProposalVersion | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [curating, setCurating] = useState(false);
  const [curateError, setCurateError] = useState('');
  const watching = useRef<number | null>(null);

  useEffect(() => {
    setSelectedId(versionId);
  }, [versionId]);

  useEffect(() => {
    // Any curation watch belongs to the version being left.
    watching.current = null;
    setCurating(false);
    if (selectedId == null) {
      setVersion(null);
      setProposal(null);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setCurateError('');
    fetchVersion(selectedId, controller.signal)
      .then(value => {
        if (!controller.signal.aborted) setVersion(value);
      })
      .catch(reason => {
        if (!controller.signal.aborted) {
          setVersion(null);
          setError(errorMessage(reason) || 'This preview could not be loaded.');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [selectedId]);

  const proposalId = version?.proposal_id ?? null;
  useEffect(() => {
    if (proposalId == null) return undefined;
    const controller = new AbortController();
    fetchProposal(proposalId, controller.signal)
      .then(value => {
        if (!controller.signal.aborted) setProposal(value);
      })
      .catch(() => {
        // The version list and status are context; the preview still renders.
        if (!controller.signal.aborted) setProposal(null);
      });
    return () => controller.abort();
  }, [proposalId, refreshKey]);

  const watchCuration = useCallback(async (from: ProposalVersion) => {
    watching.current = from.id;
    setCurating(true);
    try {
      const final = await waitForCuration(from, {
        onUpdate: value => {
          if (watching.current === from.id) setVersion(value);
        },
        cancelled: () => watching.current !== from.id,
      });
      if (watching.current === from.id) setVersion(final);
    } catch (reason) {
      if (watching.current === from.id)
        setCurateError(errorMessage(reason) || 'Curation could not be read.');
    } finally {
      if (watching.current === from.id) {
        watching.current = null;
        setCurating(false);
      }
    }
  }, []);

  // A version opened while its headlines are still being curated is watched.
  useEffect(() => {
    if (
      version &&
      !curating &&
      watching.current == null &&
      pendingCurations(version) > 0
    ) {
      void watchCuration(version);
    }
  }, [version, curating, watchCuration]);

  const curate = async () => {
    if (!version) return;
    setCurateError('');
    setCurating(true);
    try {
      const { job } = await curateVersion(version.id);
      if (job && isJobFailed(job)) {
        setCurateError(jobMessage(job));
        setCurating(false);
        return;
      }
    } catch (reason) {
      setCurateError(errorMessage(reason) || 'Curation could not be started.');
      setCurating(false);
      return;
    }
    await watchCuration(version);
  };

  const preview = version?.preview;
  const status = proposal?.status;
  const actionable = !status || status === 'open';
  const versions = [...(proposal?.versions || [])].sort(
    (a, b) => a.number - b.number,
  );
  const pending = version ? pendingCurations(version) : 0;
  const title = preview?.title || proposal?.title || 'Proposal';
  const coverage = preview?.data_status;
  const gaps = coverage?.gaps || [];

  return (
    <Drawer
      open={versionId != null}
      onClose={onClose}
      size={1100}
      styles={{ wrapper: { maxWidth: '100vw' } }}
      destroyOnHidden
      className="blueprint-builder__drawer"
      title={
        version ? `${title} · version ${version.number}` : 'Proposal preview'
      }
      extra={
        version ? (
          <Space size={8} wrap>
            <Button
              type="primary"
              disabled={!actionable}
              onClick={() =>
                onSave({
                  proposalId: version.proposal_id,
                  versionId: version.id,
                  title,
                  version,
                })
              }
            >
              Save as draft
            </Button>
            <Button
              danger
              disabled={!actionable}
              onClick={() =>
                onReject({ proposalId: version.proposal_id, title })
              }
            >
              Reject
            </Button>
          </Space>
        ) : null
      }
    >
      {loading && !version ? (
        <div className="blueprint-builder__center">
          <Spin description="Loading the preview" />
        </div>
      ) : error ? (
        <Alert type="error" showIcon title={error} />
      ) : preview && version ? (
        <div className="blueprint-builder__preview" data-testid="preview">
          <Section title="Proposal">
            <div className="blueprint-builder__preview-head">
              <Typography.Title level={4}>{preview.title}</Typography.Title>
              {preview.character && (
                <Tag color="purple">{preview.character}</Tag>
              )}
              {status && <Tag color={STATUS_COLOURS[status]}>{status}</Tag>}
            </div>
            <Typography.Text type="secondary">
              {windowLine(preview.window)} · landings every Friday ·{' '}
              {plural(preview.news_counts.total, 'news item')} (
              {preview.news_counts.driver} driver, {preview.news_counts.context}{' '}
              context, {preview.news_counts.counter} counter)
            </Typography.Text>
            {versions.length > 1 && (
              <div className="blueprint-builder__versions">
                <Typography.Text type="secondary">Version</Typography.Text>
                <Select<number>
                  aria-label="Version"
                  value={version.id}
                  onChange={setSelectedId}
                  popupMatchSelectWidth={false}
                  className="blueprint-builder__version-select"
                  options={versions.map(entry => ({
                    value: entry.id,
                    label: `v${entry.number}${entry.diff ? ` · ${entry.diff}` : ''}`,
                  }))}
                />
              </div>
            )}
            {version.diff && (
              <div>
                <Typography.Text type="secondary">
                  Changed in this version: {version.diff}
                </Typography.Text>
              </div>
            )}
            {proposal?.saved && (
              <Alert
                type={
                  proposal.saved.status === 'failed'
                    ? 'error'
                    : proposal.saved.status === 'saved'
                      ? 'success'
                      : 'info'
                }
                showIcon
                title={`Save: ${proposal.saved.status}`}
                description={
                  proposal.saved.message || hasSavedExtras(proposal.saved) ? (
                    <>
                      {proposal.saved.message && (
                        <div>{proposal.saved.message}</div>
                      )}
                      <SavedExtras saved={proposal.saved} />
                    </>
                  ) : undefined
                }
              />
            )}
            {proposal?.rejection && (
              <Alert
                type="info"
                showIcon
                title={`Rejected: ${proposal.rejection.reasons
                  .map(reason => reason.replace(/_/g, ' '))
                  .join(', ')}`}
                description={proposal.rejection.note || undefined}
              />
            )}
            <div className="blueprint-builder__thesis">
              <Typography.Text
                type="secondary"
                className="blueprint-builder__label"
              >
                Admin notes: may use hindsight
              </Typography.Text>
              <div className="blueprint-builder__text">
                {preview.thesis || <em>No thesis.</em>}
              </div>
            </div>
          </Section>

          <Section
            title="Data coverage"
            extra={
              coverage ? (
                <DataBadge
                  status={coverage.status}
                  gapCount={gaps.length}
                  blockers={gaps.filter(isBlocker).length}
                />
              ) : null
            }
          >
            <DataCoverageSection
              coverage={coverage}
              check={preview.checks.find(check => check.key === 'data')}
            />
          </Section>

          <Section title="Instruments">
            <InstrumentsSection instruments={preview.instruments} />
          </Section>

          <Section title="Curves">
            <CurvesChart
              curves={preview.curves}
              turnWindow={preview.window}
              instruments={preview.instruments}
            />
          </Section>

          <Section title="Weekly grid">
            <WeeklyGrid
              instruments={preview.instruments}
              turns={preview.turns}
            />
          </Section>

          <Section
            title="News by turn"
            extra={
              <Button
                size="small"
                loading={curating}
                disabled={curating}
                onClick={() => void curate()}
              >
                {curating
                  ? `Curating headlines${pending ? ` (${pending} pending)` : ''}`
                  : 'Curate headlines'}
              </Button>
            }
          >
            {curateError && <Alert type="error" showIcon title={curateError} />}
            <NewsByTurn turns={preview.turns} />
          </Section>

          <Section title="Checks">
            <ChecksSection checks={preview.checks} />
          </Section>

          <Section title="Overlap with existing blueprints">
            <OverlapSection overlap={preview.overlap} />
          </Section>

          <Section title="Pre-grade">
            <PregradeSection pregrade={preview.pregrade} />
          </Section>

          <Section title="Trainee-facing text">
            <TraineeSection trainee={preview.trainee} />
          </Section>
        </div>
      ) : null}
    </Drawer>
  );
};

export default PreviewDrawer;
